import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    Card, CardBody, Row, Col, Table, Spinner, Input, Label, Button,
    Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalHeader, ModalBody, Badge
} from 'reactstrap';
import { Calendar, TrendingUp, TrendingDown, DollarSign, Package, Info, Eye, FileText, Download } from 'react-feather'; // ⬅️ FileText & Download
import classnames from 'classnames';
import http from '../../components/network';

const StatCard = ({ label, value, icon: Icon, color }) => (
    <div
        className="bg-white rounded-3 p-3 h-100 d-flex align-items-center gap-3"
        style={{ border: '1px solid #eef0f4', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}
    >
        <div
            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 44, height: 44, background: `${color}14`, color }}
        >
            <Icon size={20} />
        </div>
        <div className="flex-grow-1 min-w-0">
            <div className="text-muted" style={{ fontSize: 12, letterSpacing: '.02em' }}>{label}</div>
            <div className="fw-bold text-truncate" style={{ fontSize: 18, color: '#101828' }}>{value}</div>
        </div>
    </div>
);

StatCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
};

const SalesReport = () => {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false); // ⬅️ State untuk export
    const [report, setReport] = useState(null);
    const [activeTab, setActiveTab] = useState('1');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const { data } = await http.get('/pharmacy-prescription-orders/sales-report', {
                params: { start_date: startDate, end_date: endDate }
            });
            setReport(data);
        } catch (error) {
            console.error('Failed to fetch sales report', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const formatCurrency = (val) => `Rp ${parseFloat(val || 0).toLocaleString('id-ID')}`;
    const formatQty = (val) => parseFloat(val || 0).toLocaleString('id-ID', { maximumFractionDigits: 4 });

    const openOrderModal = (order) => {
        setSelectedOrder(order);
        setModalOpen(true);
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const summary = report?.summary ?? {};

    // ⬇️ ⬇️ ⬇️ HANDLER EXPORT EXCEL
    const handleExportExcel = async () => {
        if (!report) return;
        setExporting(true);
        try {
            const response = await http.get('/pharmacy-prescription-orders/sales-report/export-excel', {
                params: { start_date: startDate, end_date: endDate },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `SalesReport_${startDate}_to_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export Excel error:', err);
            alert(t('Failed to export Excel. Please try again.'));
        } finally {
            setExporting(false);
        }
    };

    // ⬇️ ⬇️ ⬇️ HANDLER EXPORT PDF
    const handleExportPDF = async () => {
        if (!report) return;
        setExporting(true);
        try {
            const response = await http.get('/pharmacy-prescription-orders/sales-report/export-pdf', {
                params: { start_date: startDate, end_date: endDate },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `SalesReport_${startDate}_to_${endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export PDF error:', err);
            alert(t('Failed to export PDF. Please try again.'));
        } finally {
            setExporting(false);
        }
    };

    const thStyle = {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#64748b',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '12px 16px',
        whiteSpace: 'nowrap'
    };

    const tdStyle = {
        fontSize: 13,
        padding: '12px 16px',
        verticalAlign: 'middle',
        borderBottom: '1px solid #f1f5f9'
    };

    return (
        <div className="p-4" style={{ background: '#f7f8fa', minHeight: '100vh' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: '#101828' }}>{t('Pharmacy Sales Report')}</h4>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        {t('Revenue, COGS, profit, and rounding differences')}
                    </p>
                </div>

                {/* ⬇️ ⬇️ ⬇️ TOMBOL EXPORT */}
                <div className="d-flex gap-2">
                    <Button
                        color="success"
                        className="d-flex align-items-center gap-2"
                        onClick={handleExportExcel}
                        disabled={exporting || !report || !report.details?.length}
                        style={{ fontWeight: 600, fontSize: 13 }}
                    >
                        {exporting ? <Spinner size="sm" /> : <FileText size={15} />}
                        {t('Export Excel')}
                    </Button>
                    <Button
                        color="danger"
                        className="d-flex align-items-center gap-2"
                        onClick={handleExportPDF}
                        disabled={exporting || !report || !report.details?.length}
                        style={{ fontWeight: 600, fontSize: 13 }}
                    >
                        {exporting ? <Spinner size="sm" /> : <Download size={15} />}
                        {t('Export PDF')}
                    </Button>
                </div>
            </div>

            {/* Filter */}
            <Card className="border-0 mb-4" style={{ boxShadow: '0 1px 3px rgba(16,24,40,.06)', borderRadius: 12 }}>
                <CardBody className="p-3">
                    <Row className="align-items-end g-3">
                        <Col md={4}>
                            <Label className="mb-1" style={{ fontSize: 12, color: '#667085', fontWeight: 600 }}>
                                {t('From Date')}
                            </Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{ fontSize: 13 }}
                            />
                        </Col>
                        <Col md={4}>
                            <Label className="mb-1" style={{ fontSize: 12, color: '#667085', fontWeight: 600 }}>
                                {t('To Date')}
                            </Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{ fontSize: 13 }}
                            />
                        </Col>
                        <Col md={4}>
                            <Button
                                color="primary"
                                className="w-100 d-flex align-items-center justify-content-center gap-2"
                                onClick={fetchReport}
                                disabled={loading}
                                style={{ fontWeight: 600, fontSize: 13 }}
                            >
                                <Calendar size={15} />
                                {loading ? t('Loading...') : t('Show Report')}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : report ? (
                <>
                    {/* Stat Cards */}
                    <Row className="g-3 mb-3">
                        <Col md={3}>
                            <StatCard label={t('Total Transactions')} value={`${summary.total_orders ?? 0} ${t('Orders')}`} icon={Package} color="#6366f1" />
                        </Col>
                        <Col md={3}>
                            <StatCard label={t('Revenue')} value={formatCurrency(summary.total_revenue)} icon={TrendingUp} color="#10b981" />
                        </Col>
                        <Col md={3}>
                            <StatCard label={t('COGS')} value={formatCurrency(summary.total_hpp)} icon={TrendingDown} color="#ef4444" />
                        </Col>
                        <Col md={3}>
                            <StatCard label={t('Gross Profit')} value={formatCurrency(summary.total_profit)} icon={DollarSign} color="#0ea5e9" />
                        </Col>
                    </Row>

                    {/* Rounding Highlight */}
                    <div
                        className="d-flex align-items-center flex-wrap gap-3 px-3 py-2 mb-4 rounded-3"
                        style={{ background: '#fffbeb', border: '1px solid #fef3c7', fontSize: 13 }}
                    >
                        <div className="d-flex align-items-center gap-2 text-warning fw-semibold">
                            <Info size={15} />
                            {t('Rounding Difference')}
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            <span className="text-muted">
                                {t('Billed Qty')} <b className="text-dark">{formatQty(summary.total_qty_billed)}</b>
                            </span>
                            <span className="text-muted">
                                {t('Used Qty')} <b className="text-dark">{formatQty(summary.total_qty_used)}</b>
                            </span>
                            <span className="text-muted">
                                {t('Difference')} <b className="text-success">+{formatQty(summary.total_qty_diff)}</b>
                            </span>
                            <span className="text-muted">
                                {t('Est. Value')} <b className="text-success">{formatCurrency(summary.total_rounding_value)}</b>
                            </span>
                        </div>
                    </div>

                    {/* Tabs + Table */}
                    <Card className="border-0" style={{ boxShadow: '0 1px 3px rgba(16,24,40,.06)', borderRadius: 12, overflow: 'hidden' }}>
                        <CardBody className="p-0">

                            <div className="p-3" style={{ borderBottom: '1px solid #eef0f4', background: '#fff' }}>
                                <Nav tabs className="border-0 p-1 d-inline-flex rounded-3" style={{ background: '#f2f4f7' }}>
                                    <NavItem>
                                        <NavLink
                                            className={classnames('border-0 rounded-2', { active: activeTab === '1' })}
                                            onClick={() => toggleTab('1')}
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: activeTab === '1' ? '#101828' : '#667085',
                                                background: activeTab === '1' ? '#ffffff' : 'transparent',
                                                boxShadow: activeTab === '1' ? '0 1px 3px rgba(16,24,40,.1)' : 'none',
                                                padding: '6px 16px',
                                                margin: 0
                                            }}
                                        >
                                            {t('Sales Details')}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames('border-0 rounded-2', { active: activeTab === '2' })}
                                            onClick={() => toggleTab('2')}
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: activeTab === '2' ? '#101828' : '#667085',
                                                background: activeTab === '2' ? '#ffffff' : 'transparent',
                                                boxShadow: activeTab === '2' ? '0 1px 3px rgba(16,24,40,.1)' : 'none',
                                                padding: '6px 16px',
                                                margin: 0
                                            }}
                                        >
                                            {t('Rounding Difference')}
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </div>

                            <TabContent activeTab={activeTab}>
                                {/* Tab 1 */}
                                <TabPane tabId="1">
                                    <div className="table-responsive">
                                        <Table className="mb-0" style={{ borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={thStyle} className="text-center">{t('No')}</th>
                                                    <th style={thStyle}>{t('Order')}</th>
                                                    <th style={thStyle}>{t('Date')}</th>
                                                    <th style={thStyle}>{t('Patient')}</th>
                                                    <th style={thStyle} className="text-end">{t('Revenue')}</th>
                                                    <th style={thStyle} className="text-end">{t('COGS')}</th>
                                                    <th style={thStyle} className="text-end">{t('Profit')}</th>
                                                    <th style={thStyle} className="text-center">{t('Action')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {report.details?.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center text-muted py-5 border-0">
                                                            {t('No transactions found for this date')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    report.details.map((row, idx) => (
                                                        <tr key={row.order_number}>
                                                            <td style={tdStyle} className="text-center text-muted">{idx + 1}</td>
                                                            <td style={tdStyle} className="fw-semibold" >{row.order_number}</td>
                                                            <td style={tdStyle} className="text-muted">{new Date(row.order_date).toLocaleDateString('id-ID')}</td>
                                                            <td style={tdStyle} >{row.patient_name}</td>
                                                            <td style={tdStyle} className="text-end fw-semibold" >
                                                                {formatCurrency(row.revenue)}
                                                            </td>
                                                            <td style={tdStyle} className="text-end text-muted" >
                                                                {formatCurrency(row.hpp)}
                                                            </td>
                                                            <td style={{ ...tdStyle, color: '#0ea5e9', fontWeight: 700 }} className="text-end">
                                                                {formatCurrency(row.profit)}
                                                            </td>
                                                            <td style={tdStyle} className="text-center">
                                                                <Button
                                                                    color="light"
                                                                    size="sm"
                                                                    className="border d-inline-flex align-items-center justify-content-center"
                                                                    style={{ borderRadius: 6, padding: '4px 10px', fontSize: 12 }}
                                                                    onClick={() => openOrderModal(row)}
                                                                >
                                                                    <Eye size={14} className="me-1" /> {t('Detail')}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </TabPane>

                                {/* Tab 2 */}
                                <TabPane tabId="2">
                                    <div className="table-responsive">
                                        <Table className="mb-0" style={{ borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={thStyle} className="text-center">{t('No')}</th>
                                                    <th style={thStyle}>{t('Order')}</th>
                                                    <th style={thStyle}>{t('Date')}</th>
                                                    <th style={thStyle}>{t('Patient')}</th>
                                                    <th style={thStyle} className="text-end">{t('Billed Qty')}</th>
                                                    <th style={thStyle} className="text-end">{t('Used Qty')}</th>
                                                    <th style={thStyle} className="text-end">{t('Difference')}</th>
                                                    <th style={thStyle} className="text-end">{t('Value')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {report.details?.filter((r) => parseFloat(r.qty_diff) > 0).length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center text-muted py-5 border-0">
                                                            {t('No rounding differences found for this period')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    report.details
                                                        .filter((r) => parseFloat(r.qty_diff) > 0)
                                                        .map((row, idx) => (
                                                            <tr key={row.order_number}>
                                                                <td style={tdStyle} className="text-center text-muted">{idx + 1}</td>
                                                                <td style={tdStyle} className="fw-semibold" >{row.order_number}</td>
                                                                <td style={tdStyle} className="text-muted">{new Date(row.order_date).toLocaleDateString('id-ID')}</td>
                                                                <td style={tdStyle} >{row.patient_name}</td>
                                                                <td style={tdStyle} className="text-end">{formatQty(row.qty_billed)}</td>
                                                                <td style={tdStyle} className="text-end">{formatQty(row.qty_used)}</td>
                                                                <td style={{ ...tdStyle, color: '#10b981', fontWeight: 700 }} className="text-end">
                                                                    +{formatQty(row.qty_diff)}
                                                                </td>
                                                                <td style={{ ...tdStyle, color: '#10b981', fontWeight: 700 }} className="text-end">
                                                                    {formatCurrency(row.rounding_value)}
                                                                </td>
                                                            </tr>
                                                        ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>

                    {/* Modal */}
                    <Modal
                        isOpen={modalOpen}
                        toggle={() => setModalOpen(false)}
                        size="xl"
                        centered
                        contentClassName="border-0"
                        style={{ borderRadius: 14 }}
                    >
                        <ModalHeader
                            toggle={() => setModalOpen(false)}
                            className="border-0 py-3 px-4"
                            style={{ borderBottom: '1px solid #eef0f4' }}
                        >
                            <div>
                                <div className="fw-bold" style={{ fontSize: 15, color: '#101828' }}>
                                    {t('Order Details')}
                                </div>
                                <div className="text-muted" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                                    {selectedOrder?.order_number}
                                </div>
                            </div>
                        </ModalHeader>
                        <ModalBody className="p-4">
                            {selectedOrder && (
                                <>
                                    <Row className="mb-3 g-2">
                                        <Col md={6}>
                                            <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Patient')}</div>
                                            <div className="fw-semibold" style={{ fontSize: 14 }}>{selectedOrder.patient_name}</div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Date')}</div>
                                            <div className="fw-semibold" style={{ fontSize: 14 }}>
                                                {new Date(selectedOrder.order_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Table size="sm" className="align-middle mb-2" style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle} className="border-0 border-bottom">{t('Item Name')}</th>
                                                <th style={thStyle} className="border-0 border-bottom">{t('Type')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Unit Price')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Billed Qty')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Used Qty')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Difference')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Total Bill')}</th>
                                                <th style={thStyle} className="border-0 border-bottom text-end">{t('Total COGS')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.items?.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    {/* BARIS UTAMA (Obat Biasa / Header Racikan) */}
                                                    <tr>
                                                        <td style={tdStyle} className="border-0 border-bottom fw-semibold">
                                                            {item.item_name}
                                                        </td>
                                                        <td style={tdStyle} className="border-0 border-bottom">
                                                            <Badge
                                                                pill
                                                                style={{
                                                                    background: item.prescription_type === 'racikan' ? '#e0f2fe' : '#f3f4f6',
                                                                    color: item.prescription_type === 'racikan' ? '#0369a1' : '#475467',
                                                                    fontWeight: 600,
                                                                    fontSize: 10
                                                                }}
                                                            >
                                                                {item.prescription_type === 'racikan' ? t('Compound') : t('Non-Compound')}
                                                            </Badge>
                                                        </td>
                                                        <td style={tdStyle} className="border-0 border-bottom text-end">{formatCurrency(item.price)}</td>
                                                        <td style={tdStyle} className="border-0 border-bottom text-end">
                                                            {formatQty(item.qty_billed)} {item.unit_name ? <span className="text-muted" style={{ fontSize: 11 }}>({item.unit_name})</span> : ''}
                                                        </td>
                                                        <td style={tdStyle} className="border-0 border-bottom text-end">
                                                            {formatQty(item.qty_used)} {item.unit_name ? <span className="text-muted" style={{ fontSize: 11 }}>({item.unit_name})</span> : ''}
                                                        </td>
                                                        <td style={{ ...tdStyle, color: item.qty_diff > 0 ? '#10b981' : '#98a2b3', fontWeight: 600 }} className="border-0 border-bottom text-end">
                                                            {item.qty_diff > 0 ? `+${formatQty(item.qty_diff)}` : '—'}
                                                        </td>
                                                        <td style={tdStyle} className="border-0 border-bottom text-end">{formatCurrency(item.subtotal)}</td>
                                                        <td style={tdStyle} className="border-0 border-bottom text-end text-danger">{formatCurrency(item.hpp)}</td>
                                                    </tr>

                                                    {/* BARIS ANAK (Bahan-bahan Racikan) */}
                                                    {item.prescription_type === 'racikan' && item.ingredients && item.ingredients.length > 0 && (
                                                        item.ingredients.map((ing) => (
                                                            <tr key={`${item.id}-${ing.item_name}`} style={{ backgroundColor: '#fafbfc' }}>
                                                                {/* Kolom 1: Nama Bahan */}
                                                                <td style={{ ...tdStyle, paddingLeft: '30px', fontSize: 12, color: '#64748b' }} className="border-0 border-bottom">
                                                                    <i className="bi bi-arrow-return-right me-2"></i>
                                                                    {ing.item_name}
                                                                </td>
                                                                {/* Kolom 2: Tipe */}
                                                                <td style={tdStyle} className="border-0 border-bottom">
                                                                    <small className="text-muted fst-italic">{t('Ingredient')}</small>
                                                                </td>
                                                                {/* Kolom 3: Harga Unit Bahan (Unit Price) */}
                                                                <td style={tdStyle} className="border-0 border-bottom text-end text-muted">
                                                                    {ing.price !== undefined && ing.price !== null ? formatCurrency(ing.price) : '—'}
                                                                </td>
                                                                {/* Kolom 4: Qty Tagih Bahan */}
                                                                <td style={tdStyle} className="border-0 border-bottom text-end text-muted">
                                                                    {ing.qty_billed ? `${formatQty(ing.qty_billed)} ${ing.unit_name ? `(${ing.unit_name})` : ''}` : '—'}
                                                                </td>
                                                                {/* Kolom 5: Qty Pakai Bahan + Unit Item */}
                                                                <td style={{ ...tdStyle, fontSize: 12 }} className="border-0 border-bottom text-end">
                                                                    {formatQty(ing.qty_used)} {ing.unit_name ? <span className="text-muted" style={{ fontSize: 11 }}>({ing.unit_name})</span> : ''}
                                                                </td>
                                                                {/* Kolom 6: Selisih */}
                                                                <td style={tdStyle} className="border-0 border-bottom text-end"></td>
                                                                {/* Kolom 7: Subtotal Tagihan Bahan */}
                                                                <td style={tdStyle} className="border-0 border-bottom text-end text-muted">
                                                                    {ing.subtotal ? formatCurrency(ing.subtotal) : '—'}
                                                                </td>
                                                                {/* Kolom 8: HPP */}
                                                                <td style={tdStyle} className="border-0 border-bottom text-end"></td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* Teks Keterangan Tambahan */}
                                    <div className="text-muted mb-4" style={{ fontSize: 11, fontStyle: 'italic', lineHeight: 1.5 }}>
                                        * {t('For compounded prescriptions: Unit Price is the price per package. Total Bill is the total cost of the compound. Total COGS is the accumulated cost of all ingredients used.')}
                                    </div>

                                    <Row className="g-2">
                                        <Col md={4}>
                                            <div className="rounded-3 p-3" style={{ background: '#ecfdf5', border: '1px solid #d1fae5' }}>
                                                <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Revenue')}</div>
                                                <div className="fw-bold" style={{ fontSize: 15, color: '#059669' }}>{formatCurrency(selectedOrder.revenue)}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="rounded-3 p-3" style={{ background: '#fef2f2', border: '1px solid #fee2e2' }}>
                                                <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('COGS')}</div>
                                                <div className="fw-bold" style={{ fontSize: 15, color: '#dc2626' }}>{formatCurrency(selectedOrder.hpp)}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="rounded-3 p-3" style={{ background: '#eff6ff', border: '1px solid #dbeafe' }}>
                                                <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('Gross Profit')}</div>
                                                <div className="fw-bold" style={{ fontSize: 15, color: '#0284c7' }}>{formatCurrency(selectedOrder.profit)}</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </ModalBody>
                    </Modal>
                </>
            ) : null}
        </div>
    );
};

export default SalesReport;